import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../model/Root';
import Layout from '../../components/Layout';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Grid,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Select,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import FormFieldText from '../../components/FormComponents/FormFieldText';
import FormFieldSwitch from '../../components/FormComponents/FormFieldSwitch';
import TextEditor from '../../components/TextEditor/TextEditor';
import { EditorState, convertFromRaw, convertToRaw, ContentState, RawDraftContentState } from 'draft-js';
import { InfoMessage } from '../../types';

function ensurePdfExtension(file: File): File {
  const originalName = file.name || 'attachment.pdf';
  const lower = originalName.toLowerCase();
  const hasDotPdf = lower.endsWith('.pdf');
  const hasBarePdf = !hasDotPdf && lower.endsWith('pdf');
  const safeName = hasDotPdf ? originalName : hasBarePdf ? originalName.slice(0, -3) + '.pdf' : originalName + '.pdf';
  if (safeName === originalName && file.type === 'application/pdf') return file;
  return new File([file], safeName, { type: 'application/pdf' });
}

const Schema = Yup.object().shape({
  title: Yup.string().max(255).required('Required'),
});

const InfoMessageEditScreen = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const {
    infoMessageStore: {
      selected,
      fetchOne,
      clearSelected,
      save,
      uploadAttachments,
      refreshAttachments,
      deleteAttachment,
      createDraft,
    },
    churchStore: { churches, fetchChurches },
    userStore: { state: authState },
  } = useMst();

  const [editorState, setEditorState] = useState<EditorState>(() => EditorState.createEmpty());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState<string | undefined>(undefined);
  const [previewName, setPreviewName] = useState<string | undefined>(undefined);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [originalHref, setOriginalHref] = useState<string | undefined>(undefined);
  const [previewCandidates, setPreviewCandidates] = useState<string[]>([]);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const getPreviewCandidates = (url: string | undefined): string[] => {
    if (!url) return [];
    const lower = url.toLowerCase();
    const candidates: string[] = [url];
    if (!lower.endsWith('.pdf') && lower.endsWith('pdf')) {
      candidates.push(url.slice(0, -3) + '.pdf');
    }
    return candidates;
  };

  const openPreviewFromUrl = async (url: string, name?: string) => {
    // Load direct URL into the viewer; avoid XHR/fetch to sidestep CORS.
    setPreviewLoading(true);
    try {
      const candidates = getPreviewCandidates(url);
      setPreviewCandidates(candidates);
      setPreviewIndex(0);
      setPreviewSrc(candidates[0]);
      setPreviewName(name ?? '');
      setOriginalHref(candidates[0]);
      setPreviewOpen(true);
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (churches.length === 0) fetchChurches('', true);
  }, [fetchChurches, churches.length]);

  useEffect(() => {
    if (authState !== 'LOGGED_IN') return;
    if (id && id !== 'create') {
      fetchOne(id);
    } else {
      clearSelected();
    }
  }, [authState, id, fetchOne, clearSelected]);

  useEffect(() => {
    if (!selected) return;
    try {
      if (selected.body) {
        const body = selected.body;
        // Try Draft.js Raw JSON first (safe parse)
        let parsed: RawDraftContentState | undefined;
        try {
          parsed = JSON.parse(body);
        } catch (_e) {
          parsed = undefined;
        }
        if (parsed && Array.isArray(parsed.blocks)) {
          setEditorState(EditorState.createWithContent(convertFromRaw(parsed)));
          return;
        }

        // Fallback: treat as plain text
        const text = String(body);
        if (text.trim().length > 0) {
          setEditorState(EditorState.createWithContent(ContentState.createFromText(text)));
          return;
        }
      }
      setEditorState(EditorState.createEmpty());
    } catch {
      setEditorState(EditorState.createEmpty());
    }
  }, [selected]);

  useEffect(() => {
    if (!selected?.id) return;
    (async () => {
      try {
        await refreshAttachments(selected.id as string);
      } catch (_e) {
        // ignore
      }
    })();
  }, [selected?.id, refreshAttachments]);

  useEffect(() => {
    // Clear any staged files when switching between messages
    setPendingFiles([]);
  }, [selected?.id]);

  useEffect(() => {
    if (!selected?.id) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshAttachments(selected.id as string);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [selected?.id, refreshAttachments]);

  useEffect(() => {
    if (!selected?.id) return;
    refreshAttachments(selected.id as string);
  }, [location.key, selected?.id, refreshAttachments]);

  const initialValues: InfoMessage = selected
    ? {
        id: selected.id,
        title: selected.title ?? '',
        body: selected.body ?? null,
        churchId: selected.churchId ?? null,
        isActive: selected.isActive,
        status: selected.status,
        updatedAt: selected.updatedAt,
      }
    : { title: '', isActive: true, churchId: null };

  const onSubmit = async (values: InfoMessage, helpers: FormikHelpers<InfoMessage>) => {
    const raw = convertToRaw(editorState.getCurrentContent());
    const body = JSON.stringify(raw);
    const savedId = await save({ ...values, id: values.id ?? (id !== 'create' ? id : undefined), body });
    const targetId = (values.id ?? savedId) as string | undefined;
    if (targetId && pendingFiles.length > 0) {
      await uploadAttachments(targetId, pendingFiles);
      await refreshAttachments(targetId);
      setPendingFiles([]);
    }
    helpers.setSubmitting(false);
    navigate('/info-messages');
  };

  return (
    <Layout title={t('infoMessages.edit') ?? 'Info-viesti - Muokkaa'}>
      <Formik
        key={initialValues?.id ?? 'new'}
        enableReinitialize
        initialValues={initialValues}
        validationSchema={Schema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, isValid, values, setFieldValue }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormFieldText name="title" label={t('infoMessages.title') ?? 'Title'} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Select
                  fullWidth
                  size="small"
                  value={values.churchId ?? 'null'}
                  onChange={(e) => setFieldValue('churchId', e.target.value === 'null' ? null : e.target.value)}
                >
                  <MenuItem value={'null'}>Ei seurakuntaa</MenuItem>
                  {churches.map((c) => (
                    <MenuItem key={c.id} value={c.id ?? ''}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12}>
                <Typography>{t('infoMessages.body') ?? 'Body'}</Typography>
                <TextEditor editorState={editorState} onChange={setEditorState} />
              </Grid>
              {!!selected?.status && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="GrayText">
                    {t('status')}: {selected.status}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} mt={2}>
                <Typography variant="h6">
                  {(t('attachments') ?? 'Attachments') +
                    ((selected?.attachments?.length ?? 0) > 0 ? ` (${selected?.attachments?.length})` : '')}
                </Typography>
                <input
                  id="attachments-input"
                  type="file"
                  accept="application/pdf,.pdf"
                  multiple
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []).filter(
                      (f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')
                    );
                    if (files.length === 0) return;
                    const normalized = files.map((f) => ensurePdfExtension(f));
                    setPendingFiles((prev) => [...prev, ...normalized]);
                    (document.getElementById('attachments-input') as HTMLInputElement).value = '';
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={async () => {
                    document.getElementById('attachments-input')?.click();
                  }}
                >
                  {t('add_attachment') ?? 'Add attachment'}
                </Button>
                <Grid container spacing={1} mt={1}>
                  {pendingFiles.length > 0 && (
                    <>
                      {pendingFiles.map((file, idx) => (
                        <Grid item xs={12} key={`pending-${idx}`} display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2">{file.name}</Typography>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => {
                              const objectUrl = URL.createObjectURL(file);
                              openPreviewFromUrl(objectUrl, file.name);
                            }}
                          >
                            {t('preview') ?? 'Preview'}
                          </Button>
                          <IconButton
                            size="small"
                            onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            ✕
                          </IconButton>
                        </Grid>
                      ))}
                    </>
                  )}
                  {(selected?.attachments ?? []).length === 0 && pendingFiles.length === 0 ? (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        {t('no_attachments') ?? 'No attachments yet'}
                      </Typography>
                    </Grid>
                  ) : (
                    <>
                      {(selected?.attachments ?? []).map((a) => {
                        const candidates = getPreviewCandidates(a.src);
                        const primaryHref = candidates[0];
                        return (
                          <Grid item xs={12} key={a.id} display="flex" alignItems="center" gap={1}>
                            <MuiLink href={primaryHref} target="_blank" rel="noreferrer">
                              {a.name}
                            </MuiLink>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                openPreviewFromUrl(primaryHref, a.name);
                              }}
                            >
                              {t('preview') ?? 'Preview'}
                            </Button>
                            <IconButton
                              size="small"
                              onClick={() => selected?.id && deleteAttachment(selected.id, a.id)}
                            >
                              ✕
                            </IconButton>
                          </Grid>
                        );
                      })}
                    </>
                  )}
                </Grid>
              </Grid>
              <Dialog
                open={previewOpen}
                onClose={() => {
                  if (previewSrc && previewSrc.startsWith('blob:')) {
                    URL.revokeObjectURL(previewSrc);
                  }
                  setPreviewOpen(false);
                  setPreviewSrc(undefined);
                  setOriginalHref(undefined);
                }}
                fullWidth
                maxWidth="lg"
              >
                <DialogTitle>{previewName ?? t('preview') ?? 'Preview'}</DialogTitle>
                <DialogContent dividers>
                  {previewLoading ? (
                    <Typography variant="body2">{t('loading') ?? 'Loading...'}</Typography>
                  ) : previewSrc ? (
                    <iframe
                      title={previewName ?? 'Attachment preview'}
                      src={previewSrc}
                      style={{ width: '100%', height: '80vh', border: 'none' }}
                    />
                  ) : (
                    <Typography variant="body2">{t('no_preview_available') ?? 'No preview available'}</Typography>
                  )}
                </DialogContent>
                <DialogActions>
                  {previewCandidates.length > 1 && previewIndex === 0 && (
                    <Button
                      onClick={() => {
                        const next = previewCandidates[1];
                        setPreviewIndex(1);
                        setPreviewSrc(next);
                        setOriginalHref(next);
                      }}
                    >
                      {t('try_alternate_url') ?? 'Try alternate URL'}
                    </Button>
                  )}
                  {(originalHref || previewSrc) && (
                    <Button component="a" href={originalHref || previewSrc} target="_blank" rel="noreferrer">
                      {t('open_in_new_tab') ?? 'Open in new tab'}
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      if (previewSrc && previewSrc.startsWith('blob:')) {
                        URL.revokeObjectURL(previewSrc);
                      }
                      setPreviewOpen(false);
                      setPreviewSrc(undefined);
                      setOriginalHref(undefined);
                    }}
                  >
                    {t('close') ?? 'Close'}
                  </Button>
                </DialogActions>
              </Dialog>
              <Grid item xs={12}>
                <FormFieldSwitch name="isActive" label={t('active') ?? 'Active'} />
              </Grid>
              <Grid item xs={12} display="flex" gap={1}>
                <Button type="submit" variant="contained" disabled={isSubmitting || !isValid}>
                  {t('save') ?? 'Save'}
                </Button>
                {id === 'create' ? (
                  <Button
                    variant="outlined"
                    disabled={isSubmitting || !isValid}
                    onClick={async () => {
                      const rawCreate = convertToRaw(editorState.getCurrentContent());
                      const bodyCreate = JSON.stringify(rawCreate);
                      const savedId = await save({ ...values, body: bodyCreate, status: 'published', isActive: true });
                      const targetId = (values.id ?? savedId) as string | undefined;
                      if (targetId && pendingFiles.length > 0) {
                        await uploadAttachments(targetId, pendingFiles);
                        await refreshAttachments(targetId);
                        setPendingFiles([]);
                      }
                      navigate('/info-messages');
                    }}
                  >
                    {t('save_publish') ?? 'Save & Publish'}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      onClick={async () => {
                        const rawPub = convertToRaw(editorState.getCurrentContent());
                        const bodyPub = JSON.stringify(rawPub);
                        const savedId = await save({ ...values, body: bodyPub, status: 'published', isActive: true });
                        const targetId = (values.id ?? savedId) as string | undefined;
                        if (targetId && pendingFiles.length > 0) {
                          await uploadAttachments(targetId, pendingFiles);
                          await refreshAttachments(targetId);
                          setPendingFiles([]);
                        }
                        navigate('/info-messages');
                      }}
                    >
                      {t('publish') ?? 'Publish'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={async () => {
                        const rawArch = convertToRaw(editorState.getCurrentContent());
                        const bodyArch = JSON.stringify(rawArch);
                        const savedId = await save({ ...values, body: bodyArch, status: 'archived', isActive: false });
                        const targetId = (values.id ?? savedId) as string | undefined;
                        if (targetId && pendingFiles.length > 0) {
                          await uploadAttachments(targetId, pendingFiles);
                          await refreshAttachments(targetId);
                          setPendingFiles([]);
                        }
                        navigate('/info-messages');
                      }}
                    >
                      {t('archive') ?? 'Archive'}
                    </Button>
                  </>
                )}
                <Button variant="outlined" onClick={() => navigate('/info-messages')}>
                  {t('cancel') ?? 'Cancel'}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Layout>
  );
});

export default InfoMessageEditScreen;
